import React, { useState } from "react";
import { Row, Col, Card, ListGroup, Button, Badge, Table } from "react-bootstrap";
import { FaDownload, FaCopy, FaCheck, FaGlobe, FaCode, FaFolder } from "react-icons/fa";
import { message } from "antd";
import { postmanCollection } from "./postmanCollectionData";

const AIServicesDocs = () => {
  // All top-level folders in the collection
  const allFolders = postmanCollection?.item || [];
  const [selectedFolder, setSelectedFolder] = useState(
    allFolders.find(f => f.name === "AI Services") || allFolders[0] || null
  );
  const services = selectedFolder?.item || [];
  const [selectedService, setSelectedService] = useState(
    (allFolders.find(f => f.name === "AI Services") || allFolders[0])?.item?.[0] || null
  );
  const [copiedText, setCopiedText] = useState(false);
  const [activeLang, setActiveLang] = useState("javascript");

  const handleFolderChange = (folder) => {
    setSelectedFolder(folder);
    setSelectedService(folder?.item?.[0] || null);
  };

  const handleExportCollection = () => {
    try {
      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(postmanCollection, null, 2));
      const downloadAnchor = document.createElement('a');
      downloadAnchor.setAttribute("href", dataStr);
      downloadAnchor.setAttribute("download", "ai_platform_postman_collection.json");
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      downloadAnchor.remove();
      message.success("Postman collection exported successfully!");
    } catch (error) {
      console.error(error);
      message.error("Failed to export Postman collection.");
    }
  };

  const handleCopyCode = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedText(true);
    message.success("Copied to clipboard!");
    setTimeout(() => setCopiedText(false), 2000);
  };

  // Generate dynamic request url snippet
  const getRequestUrl = (service) => {
    if (!service) return "";
    const rawUrl = service.request?.url?.raw || "";
    return rawUrl.replace("{{BASE_URL}}", "https://ai.idealcorp.in");
  };

  // Detect if this endpoint uses file upload (formdata)
  const isFormDataEndpoint = (service) => {
    return service?.request?.body?.mode === "formdata";
  };

  // Format request body string
  const getRequestBody = (service) => {
    if (!service || !service.request?.body?.raw) return null;
    return service.request.body.raw;
  };

  // Generate code snippets
  const getCodeSnippet = (service, lang) => {
    if (!service) return "";
    const url = getRequestUrl(service);
    const method = service.request?.method || "POST";
    const isFormData = isFormDataEndpoint(service);
    const rawBody = getRequestBody(service);
    const isGet = method === "GET";

    if (isFormData) {
      if (lang === "javascript") {
        return `// JavaScript Fetch Example (File Upload)
const callAIService = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch("${url}", {
    method: "POST",
    headers: { "X-API-KEY": "YOUR_API_KEY" },
    body: formData
  });
  const data = await response.json();
  console.log(data);
};`;
      }
      return `# Python requests Example (File Upload)
import requests

url = "${url}"
headers = { "X-API-KEY": "YOUR_API_KEY" }

with open("your_file", "rb") as f:
    response = requests.post(url, headers=headers, files={"file": f})
print(response.json())`;
    }

    if (isGet) {
      if (lang === "javascript") {
        return `// JavaScript Fetch Example
const callAIService = async () => {
  const response = await fetch("${url}", {
    method: "GET",
    headers: { "Content-Type": "application/json" }
  });
  const data = await response.json();
  console.log(data);
};`;
      }
      return `# Python requests Example
import requests

url = "${url}"
response = requests.get(url)
print(response.json())`;
    }

    // JSON body endpoint
    let formattedBody = "{}";
    try {
      formattedBody = JSON.stringify(JSON.parse(rawBody), null, 2);
    } catch (e) {
      formattedBody = rawBody || "{}";
    }

    if (lang === "javascript") {
      return `// JavaScript Fetch Example
const callAIService = async () => {
  const response = await fetch("${url}", {
    method: "${method}",
    headers: {
      "Content-Type": "application/json",
      "X-API-KEY": "YOUR_API_KEY" // Register app to obtain
    },
    body: JSON.stringify(${formattedBody.replace(/\n/g, "\n    ")})
  });
  const data = await response.json();
  console.log(data);
};`;
    } else if (lang === "python") {
      return `# Python requests Example
import requests

url = "${url}"
headers = {
    "Content-Type": "application/json",
    "X-API-KEY": "YOUR_API_KEY"  # Register app to obtain
}
data = ${formattedBody.replace(/true/g, 'True').replace(/false/g, 'False').replace(/\n/g, "\n    ")}

response = requests.${method.toLowerCase()}(url, headers=headers, json=data)
print(response.json())`;
    }
    return "";
  };

  return (
    <div className="fade-in">
      {/* Top Banner with export button */}
      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Body className="p-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h5 className="fw-bold text-dark mb-1">Developer API Documentation</h5>
            <p className="text-muted small mb-0">
              Browse all {postmanCollection?.item?.reduce((t, f) => t + (f.item?.length || 0), 0)} endpoints across System, AI Services, AI Models, and Admin groups. Export the full Postman collection to test locally.
            </p>
          </div>
          <Button variant="outline-primary" className="rounded-3 d-flex align-items-center gap-2 px-4 py-2" onClick={handleExportCollection}>
            <FaDownload /> Export Postman Collection
          </Button>
        </Card.Body>
      </Card>

      {/* Folder Group Tabs */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        {allFolders.map((folder, idx) => (
          <Button
            key={idx}
            size="sm"
            variant={selectedFolder?.name === folder.name ? "primary" : "outline-secondary"}
            className="rounded-pill d-flex align-items-center gap-1 px-3 py-1"
            onClick={() => handleFolderChange(folder)}
          >
            <FaFolder size={11} />
            {folder.name}
            <Badge bg={selectedFolder?.name === folder.name ? "light" : "secondary"}
              text={selectedFolder?.name === folder.name ? "primary" : "white"}
              className="ms-1" style={{ fontSize: "10px" }}>
              {folder.item?.length || 0}
            </Badge>
          </Button>
        ))}
      </div>

      <Row className="g-4">
        {/* Services List Sidebar */}
        <Col lg={4}>
          <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
            <Card.Header className="bg-white border-0 pt-4 px-4 pb-2">
              <h6 className="fw-bold text-dark mb-0">{selectedFolder?.name || "Endpoints"}</h6>
              <p className="text-muted extra-small mb-0">{selectedFolder?.item?.length || 0} endpoints</p>
            </Card.Header>
            <Card.Body className="p-0">
              <ListGroup variant="flush" className="pb-3">
                {services.map((service, index) => {
                  const isSelected = selectedService?.name === service.name;
                  const method = service.request?.method || "POST";
                  
                  return (
                    <ListGroup.Item
                      key={index}
                      action
                      onClick={() => setSelectedService(service)}
                      className={`d-flex flex-column border-bottom px-4 py-3 align-items-start ${
                        isSelected ? "bg-primary bg-opacity-10 active-service-item" : ""
                      }`}
                      style={{ borderLeft: isSelected ? "4px solid #4e73df" : "4px solid transparent" }}
                    >
                      <div className="d-flex w-100 justify-content-between align-items-center mb-1">
                        <span className={`fw-bold text-truncate ${isSelected ? "text-primary" : "text-dark"}`}>
                          {service.name?.replace(" API", "")}
                        </span>
                        <Badge bg={method === "GET" ? "success" : "primary"} className="font-monospace small px-2 py-1">
                          {method}
                        </Badge>
                      </div>
                      <span className="text-muted extra-small font-monospace text-truncate w-100">
                        {service.request?.url?.path ? `/${service.request.url.path.join("/")}` : ""}
                      </span>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        {/* Selected Service Docs Details */}
        <Col lg={8}>
          {selectedService ? (
            <Card className="border-0 shadow-sm rounded-4">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <h4 className="fw-bold text-dark mb-0">{selectedService.name}</h4>
                  <Badge bg={selectedService.request?.method === "GET" ? "success" : "primary"} className="px-3 py-2 rounded-pill font-monospace" style={{ fontSize: "0.85rem" }}>
                    {selectedService.request?.method || "POST"}
                  </Badge>
                </div>

                <p className="text-secondary leading-relaxed border-bottom pb-3">
                  {selectedService.request?.description || "No description provided."}
                </p>

                {/* HTTP Request URL */}
                <h6 className="fw-bold text-dark mb-2 d-flex align-items-center gap-2">
                  <FaGlobe className="text-muted" /> API HTTP Request URL
                </h6>
                <div className="font-monospace bg-light p-3 rounded-3 border mb-4 text-break text-primary fw-semibold" style={{ fontSize: "0.9rem" }}>
                  {getRequestUrl(selectedService)}
                </div>

                {/* Mock Headers */}
                <h6 className="fw-bold text-dark mb-2">Request Headers Schema</h6>
                <div className="table-responsive mb-4">
                  <Table bordered hover size="sm" className="align-middle bg-light rounded-3 overflow-hidden border">
                    <thead>
                      <tr className="bg-secondary bg-opacity-10 small">
                        <th className="px-3 py-2">Header Key</th>
                        <th className="px-3 py-2">Value Example</th>
                        <th className="px-3 py-2">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="font-monospace px-3 py-2 text-dark">Content-Type</td>
                        <td className="font-monospace px-3 py-2 text-muted">application/json</td>
                        <td className="px-3 py-2 small">System (Required)</td>
                      </tr>
                      <tr>
                        <td className="font-monospace px-3 py-2 text-dark">X-API-KEY</td>
                        <td className="font-monospace px-3 py-2 text-muted">ai_your_registered_api_key_here</td>
                        <td className="px-3 py-2 small">Auth token (Required)</td>
                      </tr>
                    </tbody>
                  </Table>
                </div>

                {/* Body Details */}
                {isFormDataEndpoint(selectedService) ? (
                  <div className="mb-4">
                    <h6 className="fw-bold text-dark mb-2">Request Body</h6>
                    <div className="bg-light border rounded-3 p-3 d-flex align-items-center gap-2 text-muted small">
                      <span className="badge bg-warning text-dark">multipart/form-data</span>
                      Send a <code>file</code> field as binary file upload. No JSON body required.
                    </div>
                  </div>
                ) : selectedService.request?.body?.raw ? (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="fw-bold text-dark mb-0">Request Payload Body JSON</h6>
                      <Button variant="light" size="sm" className="d-flex align-items-center gap-1 text-muted py-1 border" onClick={() => handleCopyCode(getRequestBody(selectedService))}>
                        <FaCopy /> Copy Payload
                      </Button>
                    </div>
                    <pre className="p-3 bg-dark text-white rounded-3 overflow-auto font-monospace mb-4" style={{ fontSize: "0.85rem", maxHeight: "250px" }}>
                      {getRequestBody(selectedService)}
                    </pre>
                  </>
                ) : null}


                {/* Code Snippets switcher */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                    <FaCode className="text-muted" /> Code Integration Snippet
                  </h6>
                  <div className="d-flex gap-1 border rounded p-1 bg-light">
                    <Button
                      variant={activeLang === "javascript" ? "primary" : "light"}
                      size="sm"
                      className="px-3 border-0 py-1"
                      onClick={() => setActiveLang("javascript")}
                    >
                      JavaScript
                    </Button>
                    <Button
                      variant={activeLang === "python" ? "primary" : "light"}
                      size="sm"
                      className="px-3 border-0 py-1"
                      onClick={() => setActiveLang("python")}
                    >
                      Python
                    </Button>
                  </div>
                </div>

                <div className="position-relative">
                  <pre className="p-3 bg-dark text-white rounded-3 overflow-auto font-monospace border-0 mb-0" style={{ fontSize: "0.82rem", lineHeight: "1.45" }}>
                    {getCodeSnippet(selectedService, activeLang)}
                  </pre>
                  <Button
                    variant="dark"
                    size="sm"
                    className="position-absolute border border-secondary text-muted bg-opacity-70 p-2"
                    style={{ top: "10px", right: "10px" }}
                    onClick={() => handleCopyCode(getCodeSnippet(selectedService, activeLang))}
                    title="Copy code snippet"
                  >
                    {copiedText ? <FaCheck className="text-success" /> : <FaCopy />}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <Card className="border-0 shadow-sm rounded-4 p-5 text-center text-muted">
              Select an endpoint from the left menu to view developer documentation schemas.
            </Card>
          )}
        </Col>
      </Row>

      <style>{`
        .active-service-item {
          background-color: rgba(78, 115, 223, 0.08) !important;
        }
        .extra-small {
          font-size: 10px;
        }
        pre {
          background-color: #1e1e2e !important;
        }
      `}</style>
    </div>
  );
};

export default AIServicesDocs;
